// Grille Commandes — style Progression Live, tap-first, branchée sur les vraies données
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAdminApi } from '../hooks/useAdminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import {
  Search, Filter, Download, FileSpreadsheet, Plus, MoreVertical, X,
  User, AlertCircle, ChevronDown, Camera, Loader2, Eye, Edit, UserPlus, Trash2, Clock,
  ChevronLeft, ChevronRight, Volume2, VolumeX, RotateCcw, Route
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { DispatchOrderModal, DispatchScanOrderModal } from '../components/dispatch/DispatchModals';
import SplitStopModal from '../components/SplitStopModal';
import {
  OrderStopsModal, RouteBlockModal, RouteBlockBadge, ArchivedStopsModal, ArchivedStopsButton, routeBlockStopIds,
} from '../components/dispatch/GridDispatchTools';
import { Split, Scissors, Link2 } from 'lucide-react';
import { playDispatchAlarm, extractReturnedStopIds } from '../../utils/dispatchAlarm';

// ------------------ CONFIG COLONNES ------------------
const ALL_COLUMNS = [
  { key: 'order_number', label: 'N° Commande', width: 'w-28', mandatory: true },
  { key: 'status', label: 'Statut', width: 'w-32', mandatory: true },
  { key: 'client', label: 'Client', width: 'w-44' },
  { key: 'driver', label: 'Chauffeur Assigné', width: 'w-44' },
  { key: 'pickup_date', label: 'Date Cueill.', width: 'w-28' },
  { key: 'pickup_time', label: 'Heure Cueill.', width: 'w-24' },
  { key: 'pickup_city', label: 'Ville Cueill.', width: 'w-36' },
  { key: 'delivery_date', label: 'Date Livr.', width: 'w-28' },
  { key: 'delivery_time', label: 'Heure Livr.', width: 'w-24' },
  { key: 'delivery_city', label: 'Ville Livr.', width: 'w-36' },
  { key: 'pallets', label: 'Pal.', width: 'w-16 text-right' },
  { key: 'weight', label: 'Poids (kg)', width: 'w-24 text-right', hidden: true },
  { key: 'notes', label: 'Notes', width: 'w-40', hidden: true },
];

const STATUS_META = {
  // Nouvelle charte de couleurs pour la Grille Commandes :
  // gris (en attente), jaune (envoyé), vert (accepté), rouge (retour SAC), brun (bloqué)
  pending:     { label: 'En attente',      class: 'bg-slate-600/30 text-slate-300 border-slate-500/40' },
  nouvelle:    { label: 'À planifier',     class: 'bg-slate-600/30 text-slate-300 border-slate-500/40' },
  envoye:      { label: 'Envoyé',          class: 'bg-yellow-500/25 text-yellow-200 border-yellow-500/50' },
  accepte:     { label: 'Accepté',         class: 'bg-green-500/25 text-green-200 border-green-500/50' },
  en_cours:    { label: 'En cours',        class: 'bg-green-500/25 text-green-200 border-green-500/50' },
  complete:    { label: 'Complété',        class: 'bg-slate-100 text-slate-900 border-slate-200' },
  retourne:    { label: 'Retour SAC',      class: 'bg-red-500/30 text-red-200 border-red-500/60' },
  blocked:     { label: 'Groupé en attente', class: 'bg-slate-600/30 text-slate-300 border-slate-500/40' }, // gris (statut par défaut)
  frozen:      { label: 'Bloqué',          class: 'bg-yellow-900/50 text-yellow-200 border-yellow-800/70' }, // brun (réservé aux vrais incidents : impayé, dispute, etc.)
  split:       { label: 'Divisée',         class: 'bg-purple-500/20 text-purple-300 border-purple-500/40' },
  cancelled:   { label: 'Annulé',          class: 'bg-red-500/10 text-red-400 border-red-500/30' },
  draft:       { label: 'Brouillon',       class: 'bg-slate-700/50 text-slate-300 border-slate-600/50' },
};

// Alias anglais → clé FR canonique (retro-compat des anciennes commandes/tests)
const STATUS_ALIAS = {
  assigned: 'envoye',
  in_progress: 'en_cours',
  completed: 'complete',
};
const normalizeStatus = (s) => STATUS_ALIAS[s] || s;

// ------------------ HELPERS ------------------
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const CLOSED_STATUSES = new Set(['complete', 'cancelled']);
// Commande d'une date passée, jamais terminée → reportée sur aujourd'hui pour être ré-attribuée
const isOverdue = (r) => !!r.pickup_date && r.pickup_date < todayIso() && !CLOSED_STATUSES.has(r.status);

function deriveRowFromOrder(order) {
  const stops = order.stops || [];
  // Backend stocke "cueillette"/"livraison" (FR). On accepte aussi pickup/delivery pour compat.
  const isPickup = (s) => s.stop_type === 'cueillette' || s.stop_type === 'pickup';
  const isDelivery = (s) => s.stop_type === 'livraison' || s.stop_type === 'delivery';
  const pickup = stops.find(isPickup) || stops[0] || {};
  const delivery = [...stops].reverse().find(isDelivery) || stops[stops.length - 1] || {};
  const driverIds = [...new Set(stops.map((s) => s.assigned_driver_id).filter(Boolean))];
  const totalPallets = stops.reduce((sum, s) => sum + (s.items?.length || 0), 0);
  const totalWeight = stops.reduce((sum, s) => sum + (s.weight_kg || 0), 0);
  const hasReturned = stops.some((s) => s.status === 'retourne' || s.block_status === 'retourne');

  return {
    id: order.id,
    order_number: order.order_number || order.id.slice(0, 8),
    hasReturned,
    // Si retour actif, statut prime en 'retourne' pour rendre le badge visuel coherent
    status: hasReturned ? 'retourne' : normalizeStatus(pickup.block_status || delivery.block_status || order.block_status || pickup.status || 'pending'),
    client: pickup.client_name || delivery.client_name || '—',
    driverIds,
    pickup_date: pickup.required_date || '',
    pickup_time: pickup.required_time || '',
    pickup_city: pickup.city || '',
    delivery_date: delivery.required_date || '',
    delivery_time: delivery.required_time || '',
    delivery_city: delivery.city || '',
    pallets: totalPallets || '',
    weight: totalWeight || '',
    notes: order.order_notes || '',
    routeBlockId: (stops.find((s) => s.route_block_id) || {}).route_block_id || null,
    _raw: order,
  };
}

// ============================================================
// MAIN PAGE
// ============================================================
const OrdersGridPage = () => {
  const { get, post, del, patch } = useAdminApi();
  const api = useMemo(() => ({ get, post, del, patch }), [get, post, del, patch]);
  const { token, user } = useAdminAuth();
  const navigate = useNavigate();

  const isDispatcher = user?.role === 'admin_repartition';
  const [viewScope, setViewScope] = useState(() => {
    // Repartiteur : par defaut sur "Mes commandes" ; les autres : "Toutes"
    const saved = localStorage.getItem('ordersGrid.viewScope');
    if (saved) return saved;
    return isDispatcher ? 'mine' : 'all';
  });
  useEffect(() => {
    localStorage.setItem('ordersGrid.viewScope', viewScope);
  }, [viewScope]);

  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    statuses: new Set(Object.keys(STATUS_META).filter((k) => k !== 'cancelled')),
    driverIds: new Set(),
    dateFrom: '',
    dateTo: '',
  });
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('ordersGrid.visibleColumns');
    if (saved) return new Set(JSON.parse(saved));
    return new Set(ALL_COLUMNS.filter((c) => !c.hidden).map((c) => c.key));
  });
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('ordersGrid.columnOrder');
    const defaultOrder = ALL_COLUMNS.map((c) => c.key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Merge : garder l'ordre sauvé, ajouter les nouvelles colonnes à la fin, retirer celles supprimées
        const validKeys = new Set(defaultOrder);
        const filtered = parsed.filter((k) => validKeys.has(k));
        const missing = defaultOrder.filter((k) => !filtered.includes(k));
        return [...filtered, ...missing];
      } catch { /* fallback */ }
    }
    return defaultOrder;
  });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null); // { orderId, x, y }
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [assignPickerFor, setAssignPickerFor] = useState(null); // { orderIds: [...], suggestedDriverId? }
  const [splittingOrder, setSplittingOrder] = useState(null); // commande source pour "Diviser"
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [stopsModalOrderId, setStopsModalOrderId] = useState(null);
  const [routeBlockModal, setRouteBlockModal] = useState(null); // { id, name }
  const [showArchived, setShowArchived] = useState(false);
  const [routeBlockNames, setRouteBlockNames] = useState({});
  const [deleting, setDeleting] = useState(false);
  // ---- Alarme retours SAC ----
  const [alarmMuted, setAlarmMuted] = useState(() => localStorage.getItem('dispatchAlarmMuted') === '1');
  const previousReturnedIdsRef = React.useRef(new Set());
  const [returnedCount, setReturnedCount] = useState(0);
  // ---- Retour vers SAC ----
  const [returnSacModal, setReturnSacModal] = useState(null); // {orderId, orderNumber}
  const [returnSacReason, setReturnSacReason] = useState('');
  const [returningSac, setReturningSac] = useState(false);
  useEffect(() => { localStorage.setItem('dispatchAlarmMuted', alarmMuted ? '1' : '0'); }, [alarmMuted]);

  // ------------ Fetch ------------
  // Grille Commandes = uniquement les commandes ATTRIBUÉES au dispatch
  // (workflow_status "attribuee_repartiteur" et suivants). On exclut donc
  // celles qui sont encore chez le service client à planifier / RDV planifiés.
  const fetchOrders = useCallback(async () => {
    const params = new URLSearchParams({ exclude_workflow: 'nouvelle,rdv_planifie,en_optimisation,merged' });
    if (viewScope === 'mine') params.set('scope', 'mine');
    const res = await get(`/dispatch/orders?${params.toString()}`);
    if (res.success) {
      const newOrders = res.data.orders || [];
      setOrders(newOrders);
      // Detection nouveaux retours SAC : joue alarme si nouveaux
      const currentReturnedIds = extractReturnedStopIds(newOrders);
      setReturnedCount(currentReturnedIds.size);
      const hasNew = [...currentReturnedIds].some(id => !previousReturnedIdsRef.current.has(id));
      if (hasNew && previousReturnedIdsRef.current.size > 0 && !alarmMuted) {
        playDispatchAlarm();
      }
      previousReturnedIdsRef.current = currentReturnedIds;
    } else {
      toast.error('Erreur chargement commandes');
    }
    setLoading(false);
  }, [get, viewScope, alarmMuted]);

  const fetchRouteBlocks = useCallback(async () => {
    const res = await get('/dispatch/route-blocks');
    if (res.success) {
      const map = {};
      (res.data.route_blocks || []).forEach((b) => { map[b.id] = b.name; });
      setRouteBlockNames(map);
    }
  }, [get]);

  const fetchDrivers = useCallback(async () => {
    const res = await get('/saas/dashboard/drivers');
    if (res.success) setDrivers(res.data || []);
  }, [get]);

  useEffect(() => {
    setLoading(true);
    fetchOrders();
    fetchDrivers();
    fetchRouteBlocks();
  }, [fetchOrders, fetchDrivers, fetchRouteBlocks]);

  const refreshAll = useCallback(() => { fetchOrders(); fetchRouteBlocks(); }, [fetchOrders, fetchRouteBlocks]);

  // Auto-refresh toutes les 15s (temps réel light — pas de WS pour simplicité)
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => { fetchOrders(); }, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchOrders]);

  // Persist column preferences
  useEffect(() => {
    localStorage.setItem('ordersGrid.visibleColumns', JSON.stringify([...visibleColumns]));
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem('ordersGrid.columnOrder', JSON.stringify(columnOrder));
  }, [columnOrder]);

  // Déplacer une colonne : direction = -1 (gauche) ou +1 (droite) parmi les colonnes VISIBLES
  const moveColumn = useCallback((key, direction) => {
    setColumnOrder((prev) => {
      // On travaille sur l'ordre complet, mais on ne se déplace que parmi les colonnes visibles
      const visibleKeysInOrder = prev.filter((k) => visibleColumns.has(k));
      const visIdx = visibleKeysInOrder.indexOf(key);
      if (visIdx < 0) return prev;
      const targetVisIdx = visIdx + direction;
      if (targetVisIdx < 0 || targetVisIdx >= visibleKeysInOrder.length) return prev;
      const swapKey = visibleKeysInOrder[targetVisIdx];
      const next = [...prev];
      const iA = next.indexOf(key);
      const iB = next.indexOf(swapKey);
      [next[iA], next[iB]] = [next[iB], next[iA]];
      return next;
    });
  }, [visibleColumns]);

  // Réordonner par drag & drop : déplace `srcKey` juste avant `dstKey`
  const reorderColumn = useCallback((srcKey, dstKey) => {
    if (srcKey === dstKey) return;
    setColumnOrder((prev) => {
      const next = prev.filter((k) => k !== srcKey);
      const dstIdx = next.indexOf(dstKey);
      if (dstIdx < 0) return prev;
      next.splice(dstIdx, 0, srcKey);
      return next;
    });
  }, []);

  // ------------ Filtering ------------
  const rows = useMemo(() => orders.map(deriveRowFromOrder), [orders]);

  const filteredRows = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return rows.filter((r) => {
      // Si statut inconnu (pas dans STATUS_META), on l'affiche toujours pour éviter les commandes fantômes
      const knownStatus = r.status in STATUS_META;
      if (knownStatus && !filters.statuses.has(r.status)) return false;
      if (filters.driverIds.size > 0) {
        if (filters.driverIds.has('unassigned')) {
          if (r.driverIds.length > 0) return false;
        } else if (!r.driverIds.some((id) => filters.driverIds.has(id))) return false;
      }
      const today = todayIso();
      const rangeIncludesToday = (!filters.dateFrom || filters.dateFrom <= today) && (!filters.dateTo || filters.dateTo >= today);
      const carriedOver = rangeIncludesToday && isOverdue(r);
      if (!carriedOver && filters.dateFrom && r.pickup_date < filters.dateFrom) return false;
      if (!carriedOver && filters.dateTo && r.pickup_date > filters.dateTo) return false;
      if (q) {
        const hay = `${r.order_number} ${r.client} ${r.pickup_city} ${r.delivery_city} ${r.notes}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filters]);

  // ------------ Stats footer ------------
  const stats = useMemo(() => ({
    count: filteredRows.length,
    totalPallets: filteredRows.reduce((s, r) => s + (Number(r.pallets) || 0), 0),
    totalWeight: filteredRows.reduce((s, r) => s + (Number(r.weight) || 0), 0),
  }), [filteredRows]);

  // ------------ Actions ------------
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === filteredRows.length) return new Set();
      return new Set(filteredRows.map((r) => r.id));
    });
  };

  const openContextMenu = (row, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      orderId: row.id,
      row,
      x: Math.min(rect.left, window.innerWidth - 240),
      y: rect.bottom + 4,
    });
  };

  const handleAssign = async (orderIds, driverId) => {
    let ok = 0, ko = 0;
    for (const id of orderIds) {
      const res = await post(`/dispatch/orders/${id}/assign`, { driver_id: driverId });
      if (res.success) ok++; else ko++;
    }
    if (ok > 0) toast.success(`${ok} commande(s) assignée(s)`);
    if (ko > 0) toast.error(`${ko} erreur(s)`);
    setAssignPickerFor(null);
    setSelectedIds(new Set());
    fetchOrders();
  };

  const handleCancel = async (orderId) => {
    if (!window.confirm('Annuler cette commande ?')) return;
    const res = await post(`/dispatch/orders/${orderId}/status`, { status: 'cancelled' });
    if (res.success) { toast.success('Commande annulée'); fetchOrders(); }
    else toast.error(res.error || 'Erreur');
  };

  const handleReturnToSac = async () => {
    if (!returnSacModal) return;
    if (!returnSacReason.trim()) { toast.error('Merci de saisir la raison du retour'); return; }
    setReturningSac(true);
    const res = await post(`/dispatch/orders/${returnSacModal.orderId}/transition`, {
      to_status: 'nouvelle',
      comment: `Retour SAC : ${returnSacReason.trim()}`,
    });
    setReturningSac(false);
    if (res.success) {
      toast.success(`Commande ${returnSacModal.orderNumber} retournée au SAC`);
      setReturnSacModal(null);
      setReturnSacReason('');
      fetchOrders();
    } else {
      toast.error(res.error || 'Retour refusé');
    }
  };

  const handleClearReturns = async (orderId, orderNumber) => {
    const res = await post(`/dispatch/orders/${orderId}/clear-returns`, {});
    if (res.success) {
      toast.success(`Retour de ${orderNumber} marqué comme traité`);
      fetchOrders();
    } else {
      toast.error(res.error || 'Erreur');
    }
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setDeleting(true);
    let ok = 0, ko = 0;
    for (const id of ids) {
      const res = await del(`/dispatch/orders/${id}`);
      if (res.success) ok++; else ko++;
    }
    setDeleting(false);
    setShowDeleteConfirm(false);
    setSelectedIds(new Set());
    if (ok > 0) toast.success(`${ok} commande(s) supprimée(s) définitivement`);
    if (ko > 0) toast.error(`${ko} erreur(s) lors de la suppression`);
    fetchOrders();
  };

  const handleSendToOptimization = async () => {
    const ids = [...selectedIds];
    if (ids.length < 2) {
      toast.error('Sélectionnez au moins 2 commandes à regrouper');
      return;
    }
    const res = await post('/dispatch/route-opt/create', { order_ids: ids, source_type: 'dispatch' });
    if (res.success) {
      toast.success(`${ids.length} commandes envoyées à l'optimisation`);
      setSelectedIds(new Set());
      fetchOrders();
      navigate(`/admin/route-optimisation?group=${res.data.group_id}`);
    } else {
      toast.error(res.error || 'Erreur lors du regroupement');
    }
  };

  const handleCreateRouteBlock = async () => {
    const chosen = orders.filter((o) => selectedIds.has(o.id));
    const stopIds = routeBlockStopIds(chosen);
    if (stopIds.length < 2) { toast.error('Sélectionnez au moins 2 arrêts à lier'); return; }
    if (chosen.some((o) => (o.stops || []).some((st) => st.route_block_id))) {
      toast.error('Une des commandes est déjà dans une route maître');
      return;
    }
    const res = await post('/dispatch/route-blocks', { stop_ids: stopIds });
    if (res.success) {
      const block = res.data.route_block;
      toast.success(`Route maître créée : ${block?.name || 'Route'}`);
      setSelectedIds(new Set());
      refreshAll();
      if (block) setRouteBlockModal({ id: block.id, name: block.name });
    } else {
      toast.error(res.error || 'Erreur lors de la liaison');
    }
  };

  const handleMoveStop = useCallback(async (stopId, targetOrderId) => {
    if (!splittingOrder) return false;
    const res = await post(`/dispatch/orders/${splittingOrder.id}/stops/${stopId}/move`, {
      target_order_id: targetOrderId || null,
    });
    if (res.success) {
      fetchOrders();
      return true;
    }
    toast.error(res.error || 'Erreur lors du déplacement');
    return false;
  }, [splittingOrder, post, fetchOrders]);

  const driverNameById = useMemo(() => {
    const map = {};
    drivers.forEach((d) => { map[d.id || d._id] = `${d.first_name || ''} ${d.last_name || ''}`.trim() || d.email; });
    return map;
  }, [drivers]);

  const cols = columnOrder
    .filter((k) => visibleColumns.has(k))
    .map((k) => ALL_COLUMNS.find((c) => c.key === k))
    .filter(Boolean);

  return (
    <div className="h-full min-h-screen bg-slate-950 text-white flex flex-col font-sans" data-testid="orders-grid-page" onClick={() => setContextMenu(null)}>
      <Header
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen((s) => !s)}
        searchValue={filters.search}
        onSearchChange={(v) => setFilters((f) => ({ ...f, search: v }))}
        onNewOrder={() => setShowCreateModal(true)}
        onScanOrder={() => setShowScanner(true)}
        onSwitchPlanning={() => navigate('/admin/planning-chauffeurs')}
        onSwitchRouteOpt={() => navigate('/admin/route-optimisation')}
        autoRefresh={autoRefresh}
        onToggleAutoRefresh={() => setAutoRefresh((v) => !v)}
        onManualRefresh={fetchOrders}
        isDispatcher={isDispatcher}
        viewScope={viewScope}
        onToggleViewScope={setViewScope}
        alarmMuted={alarmMuted}
        onToggleAlarm={() => setAlarmMuted(m => !m)}
        returnedCount={returnedCount}
        onShowArchived={() => setShowArchived(true)}
      />
      <div className="flex-1 flex overflow-hidden">
        {sidebarOpen && (
          <FiltersSidebar
            filters={filters}
            setFilters={setFilters}
            drivers={drivers}
            rows={rows}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
          />
        )}
        <main className="flex-1 flex flex-col min-w-0 bg-slate-950 overflow-hidden">
          {selectedIds.size > 0 && (
            <BatchToolbar
              count={selectedIds.size}
              onAssign={() => setAssignPickerFor({ orderIds: [...selectedIds] })}
              onDelete={() => setShowDeleteConfirm(true)}
              onSendToOptimization={handleSendToOptimization}
              onLinkRoute={handleCreateRouteBlock}
              onClear={() => setSelectedIds(new Set())}
            />
          )}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64 text-slate-500"><Loader2 className="animate-spin mr-2" /> Chargement…</div>
            ) : filteredRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500 gap-2">
                <FileSpreadsheet size={48} className="opacity-30" />
                <p className="text-sm">Aucune commande ne correspond aux filtres.</p>
              </div>
            ) : (
              <OrdersTable
                cols={cols}
                rows={filteredRows}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onRowClick={openContextMenu}
                onOpenRow={(row) => setEditingOrder(row._raw)}
                driverNameById={driverNameById}
                onMoveColumn={moveColumn}
                onReorderColumn={reorderColumn}
                routeBlockNames={routeBlockNames}
                onOpenRouteBlock={(id) => setRouteBlockModal({ id, name: routeBlockNames[id] })}
              />
            )}
          </div>
          <StatsFooter stats={stats} totalOrders={rows.length} />
        </main>
      </div>

      {contextMenu && (
        <ContextMenu
          menu={contextMenu}
          onClose={() => setContextMenu(null)}
          onAssign={() => { setAssignPickerFor({ orderIds: [contextMenu.orderId] }); setContextMenu(null); }}
          onEdit={() => { setEditingOrder(contextMenu.row._raw); setContextMenu(null); }}
          onView={() => { setEditingOrder(contextMenu.row._raw); setContextMenu(null); }}
          onSplit={() => { setSplittingOrder(contextMenu.row._raw); setContextMenu(null); }}
          onStops={() => { setStopsModalOrderId(contextMenu.orderId); setContextMenu(null); }}
          onRouteBlock={contextMenu.row.routeBlockId ? () => { setRouteBlockModal({ id: contextMenu.row.routeBlockId, name: routeBlockNames[contextMenu.row.routeBlockId] }); setContextMenu(null); } : null}
          canSplit={(contextMenu.row._raw?.stops || []).length >= 2}
          onCancel={() => { handleCancel(contextMenu.orderId); setContextMenu(null); }}
          onDelete={() => { setSelectedIds(new Set([contextMenu.orderId])); setShowDeleteConfirm(true); setContextMenu(null); }}
          onReturnSac={() => { setReturnSacModal({ orderId: contextMenu.orderId, orderNumber: contextMenu.row.order_number }); setContextMenu(null); }}
          onClearReturns={() => { handleClearReturns(contextMenu.orderId, contextMenu.row.order_number); setContextMenu(null); }}
          hasReturned={contextMenu.row.hasReturned}
        />
      )}

      {assignPickerFor && (
        <AssignDriverPickerModal
          orderIds={assignPickerFor.orderIds}
          drivers={drivers}
          onClose={() => setAssignPickerFor(null)}
          onPick={(driverId) => handleAssign(assignPickerFor.orderIds, driverId)}
        />
      )}

      {(showCreateModal || editingOrder) && (
        <DispatchOrderModal
          orderId={editingOrder?.id}
          onClose={() => { setShowCreateModal(false); setEditingOrder(null); }}
          onSave={() => { fetchOrders(); setShowCreateModal(false); setEditingOrder(null); }}
          token={token}
          selectedDate={todayIso()}
        />
      )}

      {showScanner && (
        <DispatchScanOrderModal
          token={token}
          scanning={scanning}
          setScanning={setScanning}
          scanResult={scanResult}
          setScanResult={setScanResult}
          onClose={() => { setShowScanner(false); setScanResult(null); }}
          onCreated={() => { fetchOrders(); setShowScanner(false); setScanResult(null); }}
        />
      )}

      {splittingOrder && (
        <SplitStopModal
          sourceOrder={splittingOrder}
          allOrders={orders}
          onClose={() => setSplittingOrder(null)}
          onMove={handleMoveStop}
        />
      )}

      {stopsModalOrderId && orders.find((o) => o.id === stopsModalOrderId) && (
        <OrderStopsModal
          order={orders.find((o) => o.id === stopsModalOrderId)}
          drivers={drivers}
          driverNameById={driverNameById}
          api={api}
          onClose={() => setStopsModalOrderId(null)}
          onChanged={refreshAll}
        />
      )}

      {routeBlockModal && (
        <RouteBlockModal
          blockId={routeBlockModal.id}
          blockName={routeBlockModal.name}
          orders={orders}
          drivers={drivers}
          api={api}
          onClose={() => setRouteBlockModal(null)}
          onChanged={refreshAll}
        />
      )}

      {showArchived && <ArchivedStopsModal api={api} onClose={() => setShowArchived(false)} />}

      {showDeleteConfirm && (
        <DeleteConfirmModal
          count={selectedIds.size}
          orderNumbers={filteredRows.filter((r) => selectedIds.has(r.id)).map((r) => r.order_number)}
          deleting={deleting}
          onCancel={() => !deleting && setShowDeleteConfirm(false)}
          onConfirm={handleDeleteSelected}
        />
      )}

      {returnSacModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => !returningSac && setReturnSacModal(null)} data-testid="return-sac-modal">
          <div className="bg-slate-900 border border-cyan-500/40 rounded-xl w-full max-w-md overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-800 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center text-cyan-400 shrink-0">
                <RotateCcw size={22} />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold text-base">Retourner au Service Client</h3>
                <p className="text-xs text-slate-400 mt-1">
                  Commande <strong className="text-cyan-300">{returnSacModal.orderNumber}</strong> — sera renvoyée dans <strong className="text-cyan-300">SAC · À planifier</strong> pour reprogrammation.
                </p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <label className="block text-xs font-semibold text-slate-300">
                Raison du retour <span className="text-amber-400">*</span>
              </label>
              <textarea
                autoFocus
                value={returnSacReason}
                onChange={(e) => setReturnSacReason(e.target.value)}
                placeholder="Ex : Client absent, chargement refusé, RDV à replanifier…"
                rows={3}
                className="w-full bg-slate-950 border border-slate-800 focus:border-cyan-500 text-white px-3 py-2 rounded-lg text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                data-testid="return-sac-reason"
              />
            </div>
            <div className="p-4 bg-slate-950/50 border-t border-slate-800 flex items-center justify-end gap-2">
              <button
                onClick={() => setReturnSacModal(null)}
                disabled={returningSac}
                className="px-4 py-2 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 min-h-11 disabled:opacity-50"
                data-testid="return-sac-cancel"
              >
                Annuler
              </button>
              <button
                onClick={handleReturnToSac}
                disabled={returningSac || !returnSacReason.trim()}
                className="px-4 py-2 rounded-lg text-xs font-bold bg-cyan-500 hover:bg-cyan-400 text-black min-h-11 flex items-center gap-2 disabled:opacity-60"
                data-testid="return-sac-confirm"
              >
                {returningSac ? <><Loader2 size={13} className="animate-spin" /> Envoi…</> : <><RotateCcw size={13} /> Confirmer le retour</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// HEADER
// ============================================================
const Header = ({ sidebarOpen, onToggleSidebar, searchValue, onSearchChange, onNewOrder, onScanOrder, onSwitchPlanning, onSwitchRouteOpt, autoRefresh, onToggleAutoRefresh, onManualRefresh, isDispatcher, viewScope, onToggleViewScope, alarmMuted, onToggleAlarm, returnedCount, onShowArchived }) => (
  <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex flex-wrap items-center justify-between gap-3 sticky top-0 z-30">
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-500 font-bold">PL</div>
        <div>
          <h1 className="text-lg font-bold uppercase tracking-wide text-white leading-tight">Répartition &amp; Commandes</h1>
          <p className="text-xs text-slate-400">Grille dense • Tap-first mobile-ready</p>
        </div>
      </div>
      <div className="flex items-center bg-slate-950 border border-slate-800 p-1 rounded-lg ml-2">
        <button className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-500 text-black shadow-sm" data-testid="tab-grid-active">
          <FileSpreadsheet size={14} /> Grille Commandes
        </button>
        <button onClick={onSwitchPlanning} className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800" data-testid="tab-planning-btn">
          <Clock size={14} /> Planning Chauffeurs
        </button>
        <button onClick={onSwitchRouteOpt} className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium text-slate-400 hover:text-white hover:bg-slate-800" data-testid="tab-route-opt-btn">
          <Route size={14} /> Route Optimisation
        </button>
      </div>
      {isDispatcher && (
        <div className="flex items-center bg-slate-950 border border-slate-800 p-1 rounded-lg ml-2" role="group" aria-label="Portée">
          <button
            onClick={() => onToggleViewScope('mine')}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold ${viewScope === 'mine' ? 'bg-blue-500 text-white shadow-sm' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            data-testid="scope-mine-btn"
            title="Voir uniquement les commandes qui me sont attribuées"
          >
            👤 Mes commandes
          </button>
          <button
            onClick={() => onToggleViewScope('all')}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold ${viewScope === 'all' ? 'bg-amber-500 text-black shadow-sm' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
            data-testid="scope-all-btn"
            title="Voir toutes les commandes de l'organisation"
          >
            🌐 Toutes
          </button>
        </div>
      )}
    </div>
    <div className="flex items-center gap-2.5 flex-wrap">
      <div className="relative w-64 md:w-72">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Recherche PO, client, ville..."
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full bg-slate-950 border border-slate-800 focus:border-amber-500 text-white pl-9 pr-3 py-1.5 rounded-lg text-xs placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
          data-testid="orders-search-input"
        />
      </div>
      <button
        onClick={onManualRefresh}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 min-h-11"
        data-testid="manual-refresh-btn"
        title="Rafraîchir maintenant"
      >
        <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'}`} />
        <span>{autoRefresh ? 'Live' : 'Pause'}</span>
      </button>
      {onToggleAlarm && (
        <button
          onClick={onToggleAlarm}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border min-h-11 relative ${
            alarmMuted
              ? 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'
              : returnedCount > 0
                ? 'bg-red-500/20 border-red-500/50 text-red-300 animate-pulse'
                : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
          }`}
          data-testid="toggle-dispatch-alarm-header-btn"
          title={alarmMuted ? "Activer l'alarme sonore des retours SAC" : "Couper l'alarme sonore"}
        >
          {alarmMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          <span className="hidden sm:inline">Retours: {alarmMuted ? 'OFF' : 'ON'}</span>
          {returnedCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {returnedCount}
            </span>
          )}
        </button>
      )}
      <button
        onClick={onToggleAutoRefresh}
        className={`px-2 py-2 rounded-lg text-xs font-medium border min-h-11 ${autoRefresh ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'}`}
        data-testid="toggle-auto-refresh"
        title={autoRefresh ? 'Auto-refresh actif (toutes les 15s)' : 'Auto-refresh en pause'}
      >
        {autoRefresh ? '⏸' : '▶'}
      </button>
      <button
        onClick={onToggleSidebar}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors min-h-11 ${
          sidebarOpen ? 'bg-slate-800 border-amber-500/40 text-amber-400' : 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800'
        }`}
        data-testid="toggle-filters-btn"
      >
        <Filter size={14} /><span>Filtres</span>
      </button>
      <ArchivedStopsButton onClick={onShowArchived} />
      <button
        onClick={onScanOrder}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 min-h-11"
        data-testid="scan-order-btn"
      >
        <Camera size={14} className="text-cyan-400" /><span>Scanner PDF</span>
      </button>
      <button
        onClick={onNewOrder}
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold bg-amber-500 hover:bg-amber-400 text-black shadow-sm min-h-11"
        data-testid="new-order-btn"
      >
        <Plus size={15} /><span>Nouvelle Commande</span>
      </button>
    </div>
  </div>
);

// ============================================================
// FILTERS SIDEBAR
// ============================================================
const FiltersSidebar = ({ filters, setFilters, drivers, rows, visibleColumns, setVisibleColumns }) => {
  const toggleStatus = (st) => {
    setFilters((f) => {
      const next = new Set(f.statuses);
      if (next.has(st)) next.delete(st); else next.add(st);
      return { ...f, statuses: next };
    });
  };
  const toggleDriver = (id) => {
    setFilters((f) => {
      const next = new Set(f.driverIds);
      if (next.has(id)) next.delete(id); else next.add(id);
      return { ...f, driverIds: next };
    });
  };
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Counts par statut
  const statusCounts = useMemo(() => {
    const c = {};
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  const unassignedCount = rows.filter((r) => r.driverIds.length === 0).length;

  return (
    <aside className="w-64 bg-slate-900/95 border-r border-slate-800 flex flex-col shrink-0 overflow-y-auto text-xs p-3 space-y-4">
      <div>
        <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">Raccourcis</span>
        <div className="grid grid-cols-2 gap-1.5 mt-1.5">
          <button
            onClick={() => setFilters((f) => ({ ...f, dateFrom: todayIso(), dateTo: todayIso() }))}
            className="px-2 py-1.5 rounded bg-slate-800/80 text-slate-300 border border-slate-700/60 text-left flex items-center justify-between hover:bg-slate-700"
            data-testid="filter-today"
          >
            <span>Aujourd'hui</span>
          </button>
          <button
            onClick={() => setFilters((f) => ({ ...f, dateFrom: '', dateTo: '' }))}
            className="px-2 py-1.5 rounded bg-slate-800/80 text-slate-300 border border-slate-700/60 text-left hover:bg-slate-700"
            data-testid="filter-all-dates"
          >
            Toutes dates
          </button>
          <button
            onClick={() => setFilters((f) => {
              const next = new Set(f.statuses);
              if (next.has('cancelled')) next.delete('cancelled'); else next.add('cancelled');
              return { ...f, statuses: next };
            })}
            className={`col-span-2 px-2 py-1.5 rounded border text-left flex items-center justify-between ${
              filters.statuses.has('cancelled')
                ? 'bg-red-500/15 border-red-500/40 text-red-300'
                : 'bg-slate-800/80 border-slate-700/60 text-slate-300 hover:bg-slate-700'
            }`}
            data-testid="filter-toggle-archived"
          >
            <span>📁 {filters.statuses.has('cancelled') ? 'Archives affichées' : 'Inclure les archives'}</span>
          </button>
        </div>
      </div>

      <div className="space-y-1.5">
        <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">Statut</span>
        <div className="space-y-1 bg-slate-950 p-2 rounded-lg border border-slate-800">
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <label key={key} className="flex items-center justify-between px-1.5 py-1 rounded hover:bg-slate-900 cursor-pointer text-slate-300">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.statuses.has(key)}
                  onChange={() => toggleStatus(key)}
                  className="rounded border-slate-700 text-amber-500 focus:ring-amber-500 bg-slate-900"
                  data-testid={`filter-status-${key}`}
                />
                <span className="text-xs">{meta.label}</span>
              </div>
              <span className="text-[10px] text-slate-500">{statusCounts[key] || 0}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">Chauffeurs</span>
        <div className="space-y-1 bg-slate-950 p-2 rounded-lg border border-slate-800 max-h-40 overflow-y-auto">
          <label className="flex items-center justify-between px-1.5 py-0.5 rounded hover:bg-slate-900 cursor-pointer text-amber-400 font-semibold">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={filters.driverIds.has('unassigned')}
                onChange={() => toggleDriver('unassigned')}
                className="rounded border-slate-700 text-amber-500 focus:ring-amber-500 bg-slate-900"
                data-testid="filter-unassigned"
              />
              <span>Non assignés</span>
            </div>
            <span className="bg-amber-500/20 px-1 rounded text-[10px]">{unassignedCount}</span>
          </label>
          {drivers.map((drv) => (
            <label key={drv.id} className="flex items-center gap-2 px-1.5 py-0.5 rounded hover:bg-slate-900 cursor-pointer text-slate-300">
              <input
                type="checkbox"
                checked={filters.driverIds.has(drv.id)}
                onChange={() => toggleDriver(drv.id)}
                className="rounded border-slate-700 text-amber-500 focus:ring-amber-500 bg-slate-900"
              />
              <span className="truncate text-xs">{`${drv.first_name || ''} ${drv.last_name || ''}`.trim()}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">Date Cueillette</span>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
          className="w-full bg-slate-950 border border-slate-800 text-slate-200 px-2 py-1.5 rounded text-xs"
          data-testid="filter-date-from"
        />
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
          className="w-full bg-slate-950 border border-slate-800 text-slate-200 px-2 py-1.5 rounded text-xs"
          data-testid="filter-date-to"
        />
      </div>

      <div className="space-y-1.5">
        <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">Colonnes visibles</span>
        <div className="space-y-1 bg-slate-950 p-2 rounded-lg border border-slate-800">
          {ALL_COLUMNS.map((c) => (
            <label key={c.key} className={`flex items-center gap-2 px-1 py-0.5 rounded cursor-pointer ${c.mandatory ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-900'} text-slate-300`}>
              <input
                type="checkbox"
                checked={visibleColumns.has(c.key)}
                onChange={() => !c.mandatory && toggleColumn(c.key)}
                disabled={c.mandatory}
                className="rounded border-slate-700 text-amber-500 focus:ring-amber-500 bg-slate-900"
              />
              <span className="text-xs">{c.label}{c.mandatory && ' *'}</span>
            </label>
          ))}
        </div>
      </div>
    </aside>
  );
};

// ============================================================
// BATCH TOOLBAR
// ============================================================
const BatchToolbar = ({ count, onAssign, onDelete, onSendToOptimization, onLinkRoute, onClear }) => (
  <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2 flex items-center justify-between text-xs">
    <div className="flex items-center gap-3 flex-wrap">
      <span className="font-bold text-amber-400">{count} sélectionnée{count > 1 ? 's' : ''}</span>
      <span className="text-slate-500">|</span>
      {onSendToOptimization && (
        <button
          onClick={onSendToOptimization}
          className="px-3 py-1.5 rounded bg-purple-500 hover:bg-purple-400 text-white font-bold min-h-9 flex items-center gap-1.5"
          data-testid="batch-send-optimization-btn"
          title="Regrouper les commandes sélectionnées et optimiser la route"
        >
          <Route size={13} /> Envoyer à l'optimisation
        </button>
      )}
      <button
        onClick={onLinkRoute}
        className="px-3 py-1.5 rounded bg-cyan-500 hover:bg-cyan-400 text-black font-bold min-h-9 flex items-center gap-1.5"
        data-testid="batch-link-route-btn"
        title="Lier les arrêts des commandes sélectionnées en une route maître"
      >
        <Link2 size={13} /> Lier en route maître
      </button>
      <button
        onClick={onAssign}
        className="px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-black font-bold min-h-9"
        data-testid="batch-assign-btn"
      >
        Assigner au chauffeur…
      </button>
      <button
        onClick={onDelete}
        className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white font-bold min-h-9 flex items-center gap-1.5"
        data-testid="batch-delete-btn"
        title="Supprimer définitivement les commandes sélectionnées"
      >
        <Trash2 size={13} /> Supprimer
      </button>
    </div>
    <button onClick={onClear} className="text-slate-400 hover:text-white flex items-center gap-1 text-[11px] py-2 px-2" data-testid="clear-selection-btn">
      <X size={12} /> Désélectionner
    </button>
  </div>
);

// ============================================================
// ORDERS TABLE
// ============================================================
const OrdersTable = ({ cols, rows, selectedIds, onToggleSelect, onToggleSelectAll, onRowClick, onOpenRow, driverNameById, onMoveColumn, onReorderColumn, routeBlockNames = {}, onOpenRouteBlock }) => {
  const [dragOverKey, setDragOverKey] = React.useState(null);
  return (
  <table className="w-full border-collapse text-left text-xs font-mono select-none">
    <thead className="bg-slate-900 border-b border-slate-800 text-slate-400 uppercase tracking-wider text-[10px] font-sans sticky top-0 z-20 shadow-sm">
      <tr>
        <th className="py-2 px-2.5 w-10 text-center bg-slate-900">
          <input
            type="checkbox"
            checked={selectedIds.size === rows.length && rows.length > 0}
            onChange={onToggleSelectAll}
            className="rounded border-slate-700 text-amber-500 focus:ring-amber-500 bg-slate-950 w-4 h-4"
            data-testid="select-all-checkbox"
          />
        </th>
        <th className="w-10 bg-slate-900" />
        {cols.map((col, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === cols.length - 1;
          const isDragOver = dragOverKey === col.key;
          return (
            <th
              key={col.key}
              className={`py-2 px-2.5 font-semibold text-slate-300 border-r border-slate-800/80 last:border-r-0 ${col.width} group/th relative ${isDragOver ? 'bg-amber-500/20 outline outline-2 outline-amber-500' : ''}`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/col-key', col.key);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                if (e.dataTransfer.types.includes('text/col-key')) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragOverKey !== col.key) setDragOverKey(col.key);
                }
              }}
              onDragLeave={() => setDragOverKey((k) => (k === col.key ? null : k))}
              onDrop={(e) => {
                e.preventDefault();
                const srcKey = e.dataTransfer.getData('text/col-key');
                if (srcKey && srcKey !== col.key && onReorderColumn) onReorderColumn(srcKey, col.key);
                setDragOverKey(null);
              }}
              data-testid={`col-header-${col.key}`}
              title="Glisser pour déplacer, ou utiliser les flèches"
            >
              <div className="flex items-center gap-1 cursor-grab active:cursor-grabbing">
                {onMoveColumn && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onMoveColumn(col.key, -1); }}
                    disabled={isFirst}
                    className={`shrink-0 p-0.5 rounded ${isFirst ? 'opacity-20 cursor-not-allowed' : 'text-slate-500 hover:text-amber-400 hover:bg-slate-800'} md:opacity-0 md:group-hover/th:opacity-100 transition-opacity`}
                    aria-label="Déplacer à gauche"
                    data-testid={`col-move-left-${col.key}`}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                  >
                    <ChevronLeft size={11} />
                  </button>
                )}
                <span className="flex-1 truncate">{col.label}</span>
                {onMoveColumn && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onMoveColumn(col.key, 1); }}
                    disabled={isLast}
                    className={`shrink-0 p-0.5 rounded ${isLast ? 'opacity-20 cursor-not-allowed' : 'text-slate-500 hover:text-amber-400 hover:bg-slate-800'} md:opacity-0 md:group-hover/th:opacity-100 transition-opacity`}
                    aria-label="Déplacer à droite"
                    data-testid={`col-move-right-${col.key}`}
                    draggable={false}
                    onDragStart={(e) => e.preventDefault()}
                  >
                    <ChevronRight size={11} />
                  </button>
                )}
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
    <tbody className="divide-y divide-slate-800/60 text-slate-300">
      {rows.map((row, idx) => {
        const isSelected = selectedIds.has(row.id);
        const status = STATUS_META[row.status] || { label: row.status || '—', class: 'bg-slate-700/40 text-slate-400 border-slate-600/40' };
        const driverLabel = row.driverIds.length === 0 ? null
          : row.driverIds.length === 1 ? (driverNameById[row.driverIds[0]] || '—')
          : `${row.driverIds.length} chauffeurs`;
        return (
          <tr
            key={row.id}
            className={`hover:bg-slate-800/70 group cursor-pointer ${
              row.hasReturned
                ? 'bg-red-500/20 hover:bg-red-500/30 border-l-4 border-red-500'
                : isSelected
                  ? 'bg-amber-500/10'
                  : idx % 2 === 0 ? 'bg-slate-950' : 'bg-slate-900/30'
            }`}
            data-testid={`order-row-${row.order_number}`}
          >
            <td className="py-2 px-2.5 text-center">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => { e.stopPropagation(); onToggleSelect(row.id); }}
                onClick={(e) => e.stopPropagation()}
                className="rounded border-slate-700 text-amber-500 focus:ring-amber-500 bg-slate-900 w-4 h-4"
                data-testid={`select-checkbox-${row.order_number}`}
              />
            </td>
            <td className="py-2 px-1 text-center relative">
              <button
                onClick={(e) => onRowClick(row, e)}
                className="p-2 text-slate-400 hover:text-amber-400 rounded hover:bg-slate-700/60 min-h-11 min-w-11 md:opacity-70 md:group-hover:opacity-100 transition-opacity"
                data-testid={`row-menu-${row.order_number}`}
                aria-label="Actions"
              >
                <MoreVertical size={16} />
              </button>
            </td>
            {cols.map((col) => {
              let content = row[col.key];
              if (col.key === 'status') content = <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-sans border ${status.class}`}>{status.label}</span>;
              else if (col.key === 'driver') content = driverLabel ? <span className="flex items-center gap-1.5"><User size={12} className="text-blue-400 shrink-0" /><span className="truncate">{driverLabel}</span></span> : <span className="text-amber-400/80 italic font-medium">Non assigné</span>;
              else if (col.key === 'order_number') content = (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onOpenRow(row); }}
                  className="font-bold text-amber-400 hover:text-amber-300 hover:underline text-left"
                  data-testid={`open-order-${row.order_number}`}
                >
                  {content}
                </button>
              );
              if (col.key === 'order_number' && row.routeBlockId) content = (
                <span className="inline-flex items-center flex-wrap">
                  {content}
                  <RouteBlockBadge name={routeBlockNames[row.routeBlockId]} onClick={() => onOpenRouteBlock(row.routeBlockId)} />
                </span>
              );
              else if (col.key === 'pickup_date' && isOverdue(row)) content = (
                <span className="inline-flex flex-col leading-tight" data-testid={`overdue-badge-${row.order_number}`}>
                  <span className="text-red-300 font-semibold">{content}</span>
                  <span className="text-[9px] font-bold uppercase tracking-wider text-red-400">En retard · à réattribuer</span>
                </span>
              );
              else if (col.key === 'client') content = <span className="font-sans font-medium text-white truncate block max-w-[170px]">{content || '—'}</span>;
              else if ((col.key === 'pickup_time' || col.key === 'delivery_time')) content = <span className="text-slate-300 font-semibold">{content || '—'}</span>;
              else if (col.key === 'pickup_city' || col.key === 'delivery_city') content = <span className="text-slate-200 font-sans">{content || '—'}</span>;
              else content = content || '—';
              return (
                <td key={col.key} className={`py-2 px-2.5 ${col.width}`}>
                  {content}
                </td>
              );
            })}
          </tr>
        );
      })}
    </tbody>
  </table>
  );
};

// ============================================================
// CONTEXT MENU
// ============================================================
const ContextMenu = ({ menu, onClose, onAssign, onEdit, onView, onSplit, canSplit, onStops, onRouteBlock, onCancel, onDelete, onReturnSac, onClearReturns, hasReturned }) => (
  <>
    <div className="fixed inset-0 z-40" onClick={onClose} />
    <div
      className="fixed z-50 w-52 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl py-1 text-xs"
      style={{ top: menu.y, left: menu.x }}
      onClick={(e) => e.stopPropagation()}
      data-testid="row-context-menu"
    >
      {hasReturned && (
        <>
          <button
            onClick={onClearReturns}
            className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-emerald-500/20 hover:text-emerald-300 text-emerald-400 min-h-11 font-semibold"
            data-testid="menu-clear-returns"
          >
            <UserPlus size={13} /> Marquer traité (retour)
          </button>
          <div className="my-1 border-t border-slate-800" />
        </>
      )}
      <button onClick={onAssign} className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-amber-500 hover:text-black text-slate-200 min-h-11" data-testid="menu-assign">
        <UserPlus size={13} /> Attribuer
      </button>
      <button onClick={onEdit} className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-slate-800 text-slate-200 min-h-11" data-testid="menu-edit">
        <Edit size={13} /> Modifier
      </button>
      <button onClick={onView} className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-slate-800 text-slate-200 min-h-11" data-testid="menu-view">
        <Eye size={13} /> Voir détails
      </button>
      <button
        onClick={canSplit ? onSplit : undefined}
        disabled={!canSplit}
        title={canSplit ? 'Déplacer un arrêt vers une autre commande' : 'La commande doit avoir au moins 2 arrêts'}
        className={`w-full px-3 py-2.5 flex items-center gap-2 min-h-11 ${canSplit ? 'hover:bg-slate-800 text-slate-200' : 'text-slate-600 cursor-not-allowed'}`}
        data-testid="menu-split"
      >
        <Split size={13} /> Diviser l'arrêt
      </button>
      <button onClick={onStops} className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-slate-800 text-slate-200 min-h-11" data-testid="menu-stops">
        <Scissors size={13} /> Arrêts : séparer / assigner
      </button>
      {onRouteBlock && (
        <button onClick={onRouteBlock} className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-cyan-500/20 text-cyan-300 min-h-11" data-testid="menu-route-block">
          <Route size={13} /> Route maître…
        </button>
      )}
      <div className="my-1 border-t border-slate-800" />
      <button onClick={onReturnSac} className="w-full px-3 py-2.5 flex items-center gap-2 text-cyan-400 hover:bg-cyan-500/20 hover:text-cyan-300 min-h-11" data-testid="menu-return-sac">
        <RotateCcw size={13} /> Retour au SAC
      </button>
      <button onClick={onCancel} className="w-full px-3 py-2.5 flex items-center gap-2 text-orange-400 hover:bg-orange-500/20 hover:text-orange-300 min-h-11" data-testid="menu-cancel">
        <X size={13} /> Annuler
      </button>
      <button onClick={onDelete} className="w-full px-3 py-2.5 flex items-center gap-2 text-red-400 hover:bg-red-500/20 hover:text-red-300 min-h-11" data-testid="menu-delete">
        <Trash2 size={13} /> Supprimer…
      </button>
    </div>
  </>
);

// ============================================================
// ASSIGN DRIVER PICKER MODAL (tap-first)
// ============================================================
const AssignDriverPickerModal = ({ orderIds, drivers, onClose, onPick }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="assign-picker-modal">
    <div className="bg-slate-900 border border-slate-800 rounded-t-2xl sm:rounded-xl w-full sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
      <div className="p-4 border-b border-slate-800 flex items-center justify-between">
        <div>
          <h3 className="text-white font-bold text-base">Attribuer à un chauffeur</h3>
          <p className="text-xs text-slate-400 mt-0.5">{orderIds.length} commande{orderIds.length > 1 ? 's' : ''} sélectionnée{orderIds.length > 1 ? 's' : ''}</p>
        </div>
        <button onClick={onClose} className="p-2 text-slate-400 hover:text-white min-h-11 min-w-11" data-testid="close-assign-picker"><X size={20} /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {drivers.length === 0 && <p className="text-slate-500 text-sm text-center py-8">Aucun chauffeur disponible.</p>}
        {drivers.map((drv) => (
          <button
            key={drv.id}
            onClick={() => onPick(drv.id)}
            className="w-full bg-slate-800 hover:bg-amber-500/20 hover:border-amber-500 border border-slate-700 rounded-lg p-3 flex items-center gap-3 text-left transition-colors min-h-16"
            data-testid={`pick-driver-${drv.id}`}
          >
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-amber-500 font-bold shrink-0">
              {(drv.first_name?.[0] || '?').toUpperCase()}{(drv.last_name?.[0] || '').toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-white text-sm truncate">{`${drv.first_name || ''} ${drv.last_name || ''}`.trim() || drv.email}</div>
              <div className="text-xs text-slate-400 truncate">{drv.vehicle_info || 'Sans camion assigné'}</div>
            </div>
            {drv.is_online && <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" title="En ligne" />}
          </button>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================
// STATS FOOTER
// ============================================================
const StatsFooter = ({ stats, totalOrders }) => (
  <footer className="bg-slate-900 border-t border-slate-800 px-4 py-2 flex flex-wrap items-center justify-between text-xs text-slate-400 font-sans shrink-0">
    <div className="flex items-center gap-4">
      <span>Affichage : <strong className="text-white font-mono">{stats.count}</strong> sur {totalOrders} commandes</span>
      <span>Palettes : <strong className="text-white font-mono">{stats.totalPallets}</strong></span>
      {stats.totalWeight > 0 && <span>Poids : <strong className="text-white font-mono">{stats.totalWeight} kg</strong></span>}
    </div>
    <div className="flex items-center gap-3 text-slate-500">
      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400"></span> Connecté</span>
    </div>
  </footer>
);

// ============================================================
// DELETE CONFIRM MODAL
// ============================================================
const DeleteConfirmModal = ({ count, orderNumbers, deleting, onCancel, onConfirm }) => {
  const preview = orderNumbers.slice(0, 5);
  const remaining = orderNumbers.length - preview.length;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onCancel}
      data-testid="delete-confirm-modal"
    >
      <div
        className="bg-slate-900 border border-red-500/40 rounded-xl w-full max-w-md overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-800 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center text-red-400 shrink-0">
            <AlertCircle size={22} />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-base">Supprimer définitivement ?</h3>
            <p className="text-xs text-slate-400 mt-1">
              Cette action est <strong className="text-red-300">irréversible</strong>. Les commandes et tous leurs arrêts seront effacés de la base de données.
            </p>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs font-mono">
            <div className="text-slate-400 mb-1.5 font-sans text-[11px] uppercase tracking-wider">
              {count} commande{count > 1 ? 's' : ''} à supprimer :
            </div>
            <div className="flex flex-wrap gap-1.5">
              {preview.map((num) => (
                <span key={num} className="px-2 py-0.5 rounded bg-red-500/10 border border-red-500/30 text-red-300">
                  {num}
                </span>
              ))}
              {remaining > 0 && (
                <span className="px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400">
                  +{remaining} de plus…
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="p-4 bg-slate-950/50 border-t border-slate-800 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 min-h-11 disabled:opacity-50"
            data-testid="delete-cancel-btn"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 rounded-lg text-xs font-bold bg-red-600 hover:bg-red-500 text-white border border-red-500 min-h-11 flex items-center gap-2 disabled:opacity-60"
            data-testid="delete-confirm-btn"
          >
            {deleting ? <><Loader2 size={13} className="animate-spin" /> Suppression…</> : <><Trash2 size={13} /> Oui, supprimer</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrdersGridPage;
